import { useTranslation } from "react-i18next";
import {
  InlineNotification,
  Select,
  SelectItem,
  Stack,
  Tag,
  Tile,
} from "@carbon/react";
import type { BuyerShare, ImportPreview, ImportResolveType } from "../../types";
import UnresolvedValuesPanel from "./UnresolvedValuesPanel";
import { describeIssue } from "./importMessages";

type Option = { id: string; label: string };

type Props = {
  preview: ImportPreview;
  options: Record<ImportResolveType | "NONE", Option[]>;
  isResolving: boolean;
  onResolve: (resolutions: Record<string, Record<string, string>>) => void;
  /** Who gets the items the file assigns to nobody; "" while nobody is chosen. */
  unassignedBuyerId: string;
  onUnassignedBuyerChange: (userId: string) => void;
};

/** A list inside a notification: the repeated lines, the lines imported before. */
const IdentityList = ({
  values,
  suffix,
}: {
  values: string[];
  suffix?: (index: number) => string;
}) => (
  <ul className="pr-import__notification-list">
    {values.map((value, index) => (
      <li key={value}>
        <code className="pr-import__code">{value}</code>
        {suffix ? ` ${suffix(index)}` : null}
      </li>
    ))}
  </ul>
);

/**
 * What the file would create, and what still needs the person importing.
 *
 * Three answers and nothing else: how many items, which buyers get them, and anything that blocks
 * the import or wants a decision. How the file was read lives in `ImportTechnicalDetails`.
 */
const PreviewStep = ({
  preview,
  options,
  isResolving,
  onResolve,
  unassignedBuyerId,
  onUnassignedBuyerChange,
}: Props) => {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => i18n.exists(key);

  const buyers = preview.buyers ?? [];
  const inFileDuplicates = preview.inFileDuplicates.slice(0, 20);
  const skippedRows = preview.issues.filter(
    (issue) => issue.severity === "ERROR"
  );

  const unassigned = buyers.find((share) => !share.userId && !share.fileValue);
  const userLabel = (id: string) =>
    options.USER.find((user) => user.id === id)?.label ?? id;

  const nothingToReview =
    !unassigned &&
    preview.inFileDuplicates.length === 0 &&
    preview.unresolvedValues.length === 0 &&
    preview.historyDuplicates.length === 0 &&
    preview.rowsSkipped === 0 &&
    skippedRows.length === 0;

  const buyerName = (share: BuyerShare) => {
    if (share.userId) return userLabel(share.userId);
    if (share.fileValue) return share.fileValue;
    return unassignedBuyerId
      ? userLabel(unassignedBuyerId)
      : t("purchaseRequests.import.summary.noBuyer");
  };

  return (
    <Tile className="new-request-section pr-import__summary">
      <h2 className="new-request-section__title">
        {`2. ${t("purchaseRequests.import.summary.title")}`}
      </h2>

      <div className="pr-import__summary-grid">
        <div className="pr-import__summary-block">
          <p className="pr-import__summary-label">
            {t("purchaseRequests.import.summary.items")}
          </p>
          <p
            className="pr-import__summary-value"
            data-testid="pr-import-item-count"
          >
            {preview.itemCount}
          </p>
          {preview.groupCount > 1 && (
            <p className="pr-import__hint">
              {t("purchaseRequests.import.summary.groups", {
                count: preview.groupCount,
              })}
            </p>
          )}
        </div>

        {buyers.length > 0 && (
          <div
            className="pr-import__summary-block"
            data-testid="pr-import-buyers"
          >
            <p className="pr-import__summary-label">
              {t("purchaseRequests.import.summary.buyers")}
            </p>
            <ul className="pr-import__buyer-list">
              {buyers.map((share) => (
                <li
                  key={`${share.userId}-${share.fileValue}`}
                  className="pr-import__buyer"
                >
                  <span className="pr-import__buyer-name">
                    {buyerName(share)}
                    {!share.userId && share.fileValue && (
                      <Tag type="warm-gray" size="sm">
                        {t("purchaseRequests.import.summary.unmatched")}
                      </Tag>
                    )}
                    {share === unassigned && unassignedBuyerId && (
                      <Tag type="blue" size="sm">
                        {t("purchaseRequests.import.summary.chosen")}
                      </Tag>
                    )}
                  </span>
                  <span className="pr-import__muted">
                    {share.itemCount}{" "}
                    {t("purchaseRequests.import.summary.itemsUnit", {
                      count: share.itemCount,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <h3 className="pr-import__title">
        {t("purchaseRequests.import.attention.title")}
      </h3>

      {nothingToReview ? (
        <InlineNotification
          kind="success"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.attention.none")}
        />
      ) : (
        <Stack gap={5}>
          {inFileDuplicates.length > 0 && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              className="pr-import__notification"
              title={t("purchaseRequests.import.repeatedLines")}
              subtitle={t("purchaseRequests.import.repeatedLinesHint")}
            >
              <IdentityList
                values={inFileDuplicates.map(
                  (duplicate) => duplicate.identityValue
                )}
                suffix={(index) =>
                  t("purchaseRequests.import.rowsList", {
                    rows: inFileDuplicates[index].rows.join(", "),
                  })
                }
              />
            </InlineNotification>
          )}

          {unassigned && (
            <Tile className="pr-import__panel">
              <Select
                id="pr-import-unassigned-buyer"
                labelText={t("purchaseRequests.import.attention.unassigned")}
                helperText={t(
                  "purchaseRequests.import.attention.unassignedHint",
                  {
                    count: unassigned.itemCount,
                  }
                )}
                value={unassignedBuyerId}
                onChange={(event) =>
                  onUnassignedBuyerChange(event.target.value)
                }
              >
                <SelectItem
                  value=""
                  text={t("purchaseRequests.import.chooseOne")}
                />
                {options.USER.map((user) => (
                  <SelectItem key={user.id} value={user.id} text={user.label} />
                ))}
              </Select>
            </Tile>
          )}

          <UnresolvedValuesPanel
            values={preview.unresolvedValues}
            options={options}
            isResolving={isResolving}
            onResolve={onResolve}
          />

          {(preview.rowsSkipped > 0 || skippedRows.length > 0) && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              className="pr-import__notification"
              title={t("purchaseRequests.import.attention.skippedRows", {
                count: Math.max(preview.rowsSkipped, skippedRows.length),
              })}
            >
              <ul className="pr-import__notification-list">
                {skippedRows.slice(0, 20).map((issue, index) => (
                  <li key={`${issue.rowNumber}-${issue.column}-${index}`}>
                    {t("purchaseRequests.import.rowLabel", {
                      row: issue.rowNumber,
                    })}{" "}
                    {describeIssue(issue, t, exists)}
                  </li>
                ))}
              </ul>
            </InlineNotification>
          )}

          {preview.historyDuplicates.length > 0 && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              className="pr-import__notification"
              title={t("purchaseRequests.import.alreadyImported")}
              subtitle={t("purchaseRequests.import.alreadyImportedHint")}
            >
              <IdentityList
                values={preview.historyDuplicates
                  .slice(0, 20)
                  .map((duplicate) => duplicate.identityValue)}
              />
            </InlineNotification>
          )}
        </Stack>
      )}
    </Tile>
  );
};

export default PreviewStep;
